import React from "react";
import {
  Avatar,
  Box,
  Button,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { getAvatarColor } from "helpers/avatarHelpers";

const GroupedSidebarNav = ({
  groups = [],
  value,
  onChange,
  sx,
  viewersByTab = {},
}) => {
  const theme = useTheme();
  
  const handleSelect = (nextValue) => {
    if (!onChange) return;
    onChange(nextValue);
  };

  return (
    <Box
      sx={{
        minWidth: { xs: "100%", sm: 240 },
        bgcolor: theme.palette.background.default,
        borderRadius: { xs: "12px", sm: "0 12px 12px 0" },
        border: `1px solid ${theme.palette.primary.border2}`,
        p: "0.75rem",
        mt: "0.25rem",
        boxShadow: theme.shadows[3],
        display: "flex",
        flexDirection: "column",
        gap: 1,
        overflowY: "auto",
        ...sx,
      }}
    >
      {groups.map((group) => (
        <Box key={group.title} sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          <Typography
            variant="body2"
            sx={{
              px: 1,
              pt: 0.5,
              pb: 0.25,
              fontWeight: 700,
              color: theme.palette.primary.main,
              textTransform: "uppercase",
              letterSpacing: "0.04em",
              fontSize: "0.82rem",
            }}
          >
            {group.title}
          </Typography>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25, pl: 1 }}>
            {group.items.map((item) => {
              const isSelected = value === item.value;
              const tabViewers = Array.isArray(viewersByTab[item.label])
                ? viewersByTab[item.label]
                : [];
              return (
                <Button
                  key={`${group.title}-${item.value}`}
                  onClick={() => handleSelect(item.value)}
                  variant="text"
                  sx={{
                    justifyContent: "flex-start",
                    textTransform: "none",
                    borderRadius: "8px",
                    pl: 2,
                    pr: 1.25,
                    py: 0.9,
                    color: isSelected ? theme.palette.primary.main : theme.palette.text.secondary,
                    bgcolor: isSelected ? theme.palette.action.selected : "transparent",
                    fontWeight: isSelected ? 700 : 500,
                    fontSize: "0.95rem",
                    "&:hover": {
                      bgcolor: isSelected
                        ? theme.palette.action.selected
                        : theme.palette.action.hover,
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      width: "100%",
                    }}
                  >
                    <Typography
                      component="span"
                      sx={{
                        flex: 1,
                        textAlign: "left",
                        fontSize: "0.95rem",
                        fontWeight: isSelected ? 700 : 500,
                        color: isSelected
                          ? theme.palette.primary.main
                          : theme.palette.text.secondary,
                      }}
                    >
                      {item.label}
                    </Typography>
                    {tabViewers.length > 0 && (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.25,
                          ml: 1,
                        }}
                      >
                        {tabViewers.map((viewer) => (
                          <Tooltip
                            key={viewer.userId}
                            title={`${viewer.name} is viewing ${item.label}`}
                            arrow
                            placement="top"
                          >
                            <Avatar
                              src={viewer.imageThumbnail}
                              alt={viewer.name}
                              sx={{
                                width: 20,
                                height: 20,
                                fontSize: "0.6rem",
                                borderRadius: "6px",
                                border: `1.5px solid ${theme.palette.background.default}`,
                                bgcolor: getAvatarColor(viewer.name),
                              }}
                            >
                              {viewer.name?.charAt(0)?.toUpperCase()}
                            </Avatar>
                          </Tooltip>
                        ))}
                      </Box>
                    )}
                  </Box>
                </Button>
              );
            })}
          </Box>
        </Box>
      ))}
    </Box>
  );
};

export default GroupedSidebarNav;